// CommentaryEditor — markdown textarea for the analyst's commentary.
// Local state mirrors the `value` prop; onChange fires on every keystroke
// so the parent can debounce; onBlur fires after focus leaves (parent persists).
//
// Tier 3 §5.1: optional `aiDraftValue` marks the commentary as AI-drafted.
// Shows a badge + "Reset to AI draft" affordance when the user has edited
// away from the AI baseline.

import { useState, useEffect } from "react";

interface Props {
  value: string;
  onChange: (text: string) => void;
  onBlur?: (text: string) => void;
  disabled?: boolean;
  aiDraftValue?: string;
}

const PLACEHOLDER =
  "What changed this period? What's working? What's next?";

export function CommentaryEditor({ value, onChange, onBlur, disabled = false, aiDraftValue }: Props) {
  const [local, setLocal] = useState(value);

  // Sync when parent changes the value from outside (e.g. switching drafts)
  useEffect(() => {
    setLocal(value);
  }, [value]);

  const hasAiDraft = aiDraftValue !== undefined && aiDraftValue.length > 0;
  const userEdited = hasAiDraft && local !== aiDraftValue;

  function resetToAiDraft() {
    if (!hasAiDraft) return;
    setLocal(aiDraftValue!);
    onChange(aiDraftValue!);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <label
          style={{
            fontSize: "var(--fs-xs)",
            fontFamily: "var(--font-mono)",
            textTransform: "uppercase",
            letterSpacing: "var(--letter-label)",
            color: "var(--color-bone-dim)",
            fontWeight: 600,
          }}
        >
          Commentary
          {hasAiDraft && (
            <span style={{
              marginLeft: 8,
              textTransform: "none",
              letterSpacing: "normal",
              fontWeight: 400,
              color: "var(--color-bone-dim)",
            }}>
              ✨ AI draft — edit before approving
            </span>
          )}
        </label>
        {userEdited && (
          <button
            type="button"
            onClick={resetToAiDraft}
            disabled={disabled}
            style={{
              fontSize: "var(--fs-xs)",
              fontFamily: "var(--font-mono)",
              background: "transparent",
              color: "var(--color-bone-dim)",
              border: "none",
              cursor: disabled ? "not-allowed" : "pointer",
              textDecoration: "underline",
              padding: 0,
            }}
          >
            Reset to AI draft
          </button>
        )}
      </div>
      <textarea
        value={local}
        disabled={disabled}
        placeholder={PLACEHOLDER}
        rows={8}
        onChange={(e) => {
          setLocal(e.target.value);
          onChange(e.target.value);
        }}
        onBlur={() => onBlur?.(local)}
        style={{
          width: "100%",
          padding: 12,
          fontFamily: "var(--font-sans)",
          fontSize: "var(--fs-sm)",
          lineHeight: 1.6,
          background: "rgba(255,255,255,0.03)",
          color: disabled ? "var(--color-bone-dim)" : "var(--color-bone)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "var(--radius-sm)",
          resize: "vertical",
          outline: "none",
          transition: "border-color 0.15s",
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = "rgba(98,216,216,0.35)";
        }}
        onBlurCapture={(e) => {
          e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
        }}
      />
      <div
        style={{
          fontSize: "var(--fs-xs)",
          color: "var(--color-bone-dim)",
          textAlign: "right",
        }}
      >
        {local.length} chars · Markdown supported
      </div>
    </div>
  );
}
